import { GlobalStyles } from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import { makeStyles } from "@mui/styles";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowDragEndEvent,
} from "ag-grid-community";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormsContext } from "../../context/FormsContext";
import { LazyLoading } from "@/components/loaders/Loader/LazyLoading";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { ModalGridBox, listMenuColumn } from "../modal-grid";
import { ColorCircleRenderer } from "./columns-table/color-column ";
import { ButtonToShowOptionsQuestions } from "./columns-table/ButtonToShowOptionsQuestions";
import { NewAndEditSectionModal } from "./sections-modals/modal-new-and-edit-section";
import type { Section } from "../../models/Section";
import { INITIAL_VALUES_SECTION } from "../../models/SectionInitialValues";
import { DeleteSectionDialog } from "./sections-modals/modal-delete-section";
import { QuestionsAndOptionsTable } from "../formQuestionsAndOptions/formQuestionsAndOptionsTable";
import { useClone } from "../../hooks/useClone";
import { updateSectionSortOrder } from "../../repositories/FormsRepository";

const useStyles = makeStyles((theme) => ({
	root: {
		"& h3": {
			display: "inline-block",
			marginTop: 0,
			paddingBotton: 0,
			paddingLeft: 20,
			border: "none",
			userSelect: "none",
		},
		"& .content-wrapper": {
			border: "none",
		},
		"& .ag-selection-checkbox": {
			marginLeft: "10px !important",
		},
		"& .ag-row-drag": {
			cursor: "grab !important",
		},
		"& .ag-row-dragging": {
			opacity: 0.6,
			backgroundColor: "#f5f5f5 !important",
		},
		"& .ag-row-drag-move": {
			cursor: "grabbing !important",
		},
		"& .ag-row.drag-target-above::before": {
			content: "''",
			position: "absolute",
			top: "-1px",
			left: "0",
			right: "0",
			height: "2px",
			backgroundColor: theme.palette.primary.main,
			zIndex: 1000,
			boxShadow: `0 0 3px ${theme.palette.primary.main}99`,
		},
		"& .ag-row.drag-target-below::after": {
			content: "''",
			position: "absolute",
			bottom: "-1px",
			left: "0",
			right: "0",
			height: "2px",
			backgroundColor: theme.palette.primary.main,
			zIndex: 1000,
			boxShadow: `0 0 3px ${theme.palette.primary.main}99`,
		},
	},
	modalTitle: {
		backgroundColor: "#ced2cc",
		color: "#495057",
		textAlign: "center",
	},
	modalTitleCloseWO: {
		backgroundColor: "#28a745",
		color: "white",
		textAlign: "center",
	},
	modalWarning: {
		color: "white",
		backgroundColor: "#dc3545",
		textAlign: "center",
	},
	messageTitle: {
		fontSize: 15,
		fontWeight: "bold",
		color: "#0000008A",
	},
	responsive: {
		[theme.breakpoints.up("md")]: {
			padding: "0 10px 0 0 !important",
			marginTop: "0 !important",
		},
	},
	responsiveTwo: {
		[theme.breakpoints.up("md")]: {
			padding: "0 0 0 10px !important",
			marginTop: "0 !important",
		},
	},
	responsiveThree: {
		[theme.breakpoints.up("md")]: {
			padding: "0 !important",
		},
	},
	responsiveFour: {
		[theme.breakpoints.up("md")]: {
			padding: "0 10px 0 0 !important",
		},
	},
	responsiveFive: {
		[theme.breakpoints.up("md")]: {
			padding: "0 0 0 10px !important",
		},
	},
}));

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	searchFormSections: Function;
	searchForms: Function;
	isSectionsLoading: boolean;
};

export const FormSectionsTable = ({
	show,
	onHide,
	searchFormSections,
	isSectionsLoading,
	searchForms,
}: Props) => {
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const classes = useStyles();
	const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
	const [error, setError] = useState<string>("");
	const [shouldUpdateWhenExit, setShouldUpdateWhenExit] = useState(false);
	const [newMode, setNewMode] = useState<boolean>(false);

	const [openNewEditSection, setOpenNewEditSection] = useState(false);
	const [openDeleteSection, setOpenDeleteSection] = useState(false);
	const [openQuestionsAndOptionsTable, setOpenQuestionsAndOptionsTable] =
		useState(false);

	const {
		state: { formSectionsList, FormName, sectionSelectedInTable },
		actions: {
			setSectionSelectedInTable,
			setFormId,
			setSectionId,
			setSectionName,
		},
	} = useFormsContext();

	const loadingOverlayComponent = useMemo<any>(() => {
		return LazyLoading;
	}, []);

	const { CloneSection } = useClone();

	const handleClone = (SectionId: number) => {
		CloneSection({ SectionId, searchFormSections, setShouldUpdateWhenExit });
	};

	const openNewSection = () => {
		setSectionSelectedInTable(INITIAL_VALUES_SECTION);
		setNewMode(true);
		setSectionId(null);
		setSectionName("");
		setOpenNewEditSection(true);
	};

	const handleCancel = () => {
		onHide(shouldUpdateWhenExit);
	};

	const onRowDragEnd = async (params: RowDragEndEvent) => {
		if (!params.node?.data?.SectionId || !params.api) {
			// Clean up drag target classes
			const allRows = document.querySelectorAll(".ag-row");
			allRows.forEach((row) => {
				row.classList.remove("drag-target-above", "drag-target-below");
			});
			return;
		}

		let sectionIdBelow = null;
		let isDroppingAbove = false;
		let isDroppingBelow = false;

		// Check if we have a target node to determine drop position
		if (params.overNode && params.overNode.data) {
			const targetRowElement = document.querySelector(
				`[row-id="${params.overNode.id}"]`,
			);

			if (targetRowElement) {
				// Read the classes BEFORE cleaning them up
				isDroppingAbove =
					targetRowElement.classList.contains("drag-target-above");
				isDroppingBelow =
					targetRowElement.classList.contains("drag-target-below");

				if (isDroppingAbove) {
					// Insert BEFORE the target row - SP expects the section to insert BEFORE
					sectionIdBelow = params.overNode.data.SectionId;
				} else if (isDroppingBelow) {
					// Insert AFTER the target row - find the next row to insert BEFORE
					const targetIndex = params.overNode.rowIndex!;
					const nextRow = params.api.getDisplayedRowAtIndex(targetIndex + 1);
					sectionIdBelow =
						nextRow && nextRow.data ? nextRow.data.SectionId : null;
				} else {
					// Fallback - treat as dropping above
					sectionIdBelow = params.overNode.data.SectionId;
				}
			} else {
				// Fallback if can't find the element
				sectionIdBelow = params.overNode.data.SectionId;
			}
		} else if (params.overIndex !== undefined && params.overIndex >= 0) {
			// Fallback for when overNode is not available
			const targetRow = params.api.getDisplayedRowAtIndex(params.overIndex);
			if (targetRow && targetRow.data) {
				sectionIdBelow = targetRow.data.SectionId;
			}
		}

		// Clean up drag target classes AFTER reading them
		const allRows = document.querySelectorAll(".ag-row");
		allRows.forEach((row) => {
			row.classList.remove("drag-target-above", "drag-target-below");
		});

		try {
			const res = await updateSectionSortOrder(
				params.node.data.SectionId,
				sectionIdBelow,
			);

			if (res.ok) {
				searchFormSections();
				setShouldUpdateWhenExit(true);
			} else {
				setError(res.message || "Error updating section order");
			}
		} catch (error) {
			setError("Error updating section priority");
		}
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data: Section = params.node?.data;
			const result: (DefaultMenuItem | MenuItemDef)[] = [];

			if (data) {
				result.push({
					name: "Edit",
					action: () => {
						setNewMode(false);
						setSectionSelectedInTable(data);
						setSectionId(data.SectionId);
						setSectionName(data.SectionName);
						setOpenNewEditSection(true);
					},
					icon: '<i class="fas fa-edit"></i>',
				});
			}

			if (data) {
				result.push({
					name: "New",
					action: () => {
						setSectionSelectedInTable(INITIAL_VALUES_SECTION);
						setNewMode(true);
						setSectionId(null);
						setSectionName("");
						setOpenNewEditSection(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			if (data) {
				result.push({
					name: "Content",
					action: () => {
						setNewMode(false);
						setSectionSelectedInTable(data);
						setSectionId(data.SectionId);
						setSectionName(data.SectionName);
						setOpenQuestionsAndOptionsTable(true);
					},
					icon: '<i class="fas fa-table"></i>',
				});
			}

			if (data) {
				result.push({
					name: "Duplicate Section",
					action: () => {
						setSectionSelectedInTable(data);
						handleClone(data.SectionId as number);
					},
					icon: '<i class="fas fa-clone"></i>',
				});
			}

			if (data) {
				result.push({
					name: "Delete",
					action: () => {
						setNewMode(false);
						setOpenDeleteSection(true);
						setSectionSelectedInTable(data);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			const addNewSection: (DefaultMenuItem | MenuItemDef)[] = [];

			if (!data) {
				addNewSection.push({
					name: "New",
					action: () => {
						setSectionSelectedInTable(INITIAL_VALUES_SECTION);
						setNewMode(true);
						setSectionId(null);
						setSectionName("");
						setOpenNewEditSection(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			return data ? result : addNewSection;
		},
		[formSectionsList],
	);

	useEffect(() => {
		if (show) {
			setShouldUpdateWhenExit(false);
			searchFormSections();
			setColumnDefs([]);
			const newColumns: ColDef[] = [];

			newColumns.push({
				field: "",
				headerName: "",
				rowDrag: true,
				minWidth: 50,
				maxWidth: 50,
				suppressHeaderMenuButton: true,
				suppressHeaderFilterButton: true,
			});

			newColumns.push({
				field: "SectionName",
				headerName: "Section Name",
				sortable: true,
				flex: 4,
				minWidth: 130,
				cellRenderer: (params: EditableCallbackParams) => {
					return (
						<Grid2
							container
							style={{
								height: "100%",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<Grid2
								style={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
								title={params.data?.Subtitle}
								size={{ xs: 12, md: 12 }}
							>
								{params.data?.SectionName}
							</Grid2>
						</Grid2>
					);
				},
			});
			newColumns.push({
				field: "BackgroundColor",
				headerName: "Color",
				sortable: false,
				flex: 2,
				minWidth: 80,
				maxWidth: 80,
				cellRenderer: ColorCircleRenderer,
			});
			newColumns.push({
				field: "",
				headerName: "Questions / Options",
				sortable: false,
				flex: 4,
				minWidth: 150,
				maxWidth: 150,
				cellRenderer: (params: EditableCallbackParams) =>
					ButtonToShowOptionsQuestions({
						params,
						setOpenQuestionsAndOptionsTable,
					}),
			});
			newColumns.push(listMenuColumn);

			setColumnDefs(newColumns);
		}
	}, [show]);
	useEffect(() => {
		if (isSectionsLoading) {
			gridApi?.showLoadingOverlay();
		} else gridApi?.hideOverlay();
	}, [isSectionsLoading, formSectionsList, gridApi]);

	return (
		<>
			<GlobalStyles
				styles={{
					".MuiDialog-paper[aria-labelledby='form-sections-modal']": {
						width: "100%",
					},
				}}
			/>
			<MesfModal
				title={`SECTIONS - ${FormName ?? ""}`}
				open={show}
				handleClose={handleCancel}
				maxWidth="md"
				id="form-sections-modal"
			>
				<MesfModal.Content dividers>
					<ModalGridBox height="55vh" onAdd={openNewSection}>
						<div
							style={{ height: "100%", width: "100%" }}
							className={classes.root}
						>
							<AgGridReact
								rowData={formSectionsList}
								columnDefs={columnDefs}
								defaultColDef={globalDefaultColDef}
								rowHeight={38}
								headerHeight={42}
								loadingOverlayComponent={loadingOverlayComponent}
								animateRows={true}
								getContextMenuItems={(e: any) => getContextMenuItems(e)}
								onRowDoubleClicked={(event) => {
									setSectionName(event.data?.SectionName);
									setSectionId(event.data?.SectionId);
									setSectionSelectedInTable(event.data);
									setFormId(event.data?.FormId);
									setOpenNewEditSection(true);
								}}
								rowSelection="single"
								onGridReady={(params) => setGridApi(params.api)}
								getRowId={(params) => params.data.SectionId}
								rowDragEntireRow={true}
								onRowDragEnd={onRowDragEnd}
								suppressMoveWhenRowDragging={false}
								onRowDragMove={(params) => {
									// Simple approach: add CSS class to target row
									const allRows = document.querySelectorAll(".ag-row");
									allRows.forEach((row) => {
										row.classList.remove(
											"drag-target-above",
											"drag-target-below",
										);
									});

									if (params.overNode) {
										const targetRowElement = document.querySelector(
											`[row-id="${params.overNode.id}"]`,
										);
										if (targetRowElement) {
											const rect = targetRowElement.getBoundingClientRect();
											const mouseY = params.event?.clientY || 0;
											const rowMiddle = rect.top + rect.height / 2;

											if (mouseY < rowMiddle) {
												targetRowElement.classList.add("drag-target-above");
											} else {
												targetRowElement.classList.add("drag-target-below");
											}
										}
									}
								}}
								onRowDragLeave={() => {
									// Clean up classes
									const allRows = document.querySelectorAll(".ag-row");
									allRows.forEach((row) => {
										row.classList.remove(
											"drag-target-above",
											"drag-target-below",
										);
									});
								}}
							/>
						</div>
					</ModalGridBox>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 16px 16px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end" width="100%">
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={handleCancel}
							>
								Close
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<NewAndEditSectionModal
				show={openNewEditSection}
				data={sectionSelectedInTable}
				newMode={newMode}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchFormSections();
						setShouldUpdateWhenExit(true);
					}
					setOpenNewEditSection(false);
				}}
			/>
			<DeleteSectionDialog
				show={openDeleteSection}
				searchData={searchFormSections}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchFormSections();
						searchForms();
					}
					setOpenDeleteSection(false);
				}}
			/>
			<QuestionsAndOptionsTable
				show={openQuestionsAndOptionsTable}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchFormSections();
						searchForms();
					}
					setOpenQuestionsAndOptionsTable(false);
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
